import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import { appDocumentTitle } from '../../../appMeta';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { PAGE_SHELL_SCROLLABLE_TABS_SX, PageShell } from '../../../layout/PageShell';
import { CollapsibleKeteranganSplit } from '../../../ui/CollapsibleKeteranganSplit';
import { useMasterEntityMiniListQuery, useNavigationMenuQuery, useWorkOrderDetailQuery } from '../../../query/hooks';
import { queryKeys } from '../../../query/queryKeys';
import { canEditWorkOrder } from '../../SamplingSchedulesPage/samplingScheduleAccess';
import {
  filterSamplesByAirGroup,
  filterCaraSamplingReferensiByAirGroup,
  samplingPlanTabLabel,
  samplesHaveAirGroup,
  type SamplingPlanAirGroup,
} from '../samplingPlanAirGroup';
import {
  buildWorkOrderRouteHash,
  parseWorkOrderRouteHash,
  resolveSamplingPlanRouteTab,
  type WorkOrderRouteTab,
} from '../workOrderRouteHash';
import { quotationUsesSampleGroups, isMarketingPic, isSuperadminUser } from '../../QuotationsPage/quotationWorkflow';
import {
  formatBakuMutuLine,
  formatJenisPengukuranLine,
  strapiCreateSamplingPlan,
  strapiListSamplingPlansForWorkOrder,
  strapiResolveSurveyTeamsForWorkOrder,
  strapiSyncSamplingPlansForWorkOrder,
  strapiUpdateSamplingPlanChecklist,
  type SamplingChecklistFormChecklist,
  type SamplingChecklistFormOptions,
  type StrapiSamplingPlanCreateData,
  type StrapiSamplingScheduleRow,
  type StrapiSampleInformationRow,
  type StrapiWorkOrderRow,
} from '../../../api/strapiCrm';
import { strapiListDynamicEntityPage, type DynamicEntityRow } from '../../../api/strapiDynamicEntity';
import { useChecklistSectionSearch } from '../../../sampling/checklistSectionSearch';
import { strapiListUsers, strapiUserRelationConnect, type StrapiManagedUserRow } from '../../../api/strapiUsers';
import {
  applyParameterUjiMappingsToEmptySections,
  mappingAppliesToParameterIds,
  parameterDocumentIdsFromSampleLines,
  strapiListPeralatanToParameterUjiByParameterIds,
} from '../../../api/peralatanToParameterUji';
import { ChemParameterUjiCell } from '../../../components/ChemParameterUjiCell';
import { MasterMultiCheckboxGroup } from '../../../components/MasterMultiCheckboxGroup';
import { MetodaSamplingChemLabel, metodaSamplingPlainLabel } from '../../../components/MetodaSamplingChemLabel';
import {
  WorkOrderDetailIntroBlurb,
  WorkOrderDetailScheduleReadonlyFields,
  WorkOrderDetailViewFooterActions,
} from './TabScreens/WorkOrderMeta';
import {
  buildSurveyTeamDisplayEntries,
} from '../../../components/surveyScheduleCalendar';
import { scheduleDayTitle } from '../../../sampling/surveyScheduleCalendar';
import { filterSamplesForWorkOrderSamplingPlan } from '../../../pages/SamplingSchedulesPage/samplingSchedulePlans';
import {
  buildSamplingPlanCreateDataForAirGroup,
  findSamplingPlanForAirGroup,
  findSamplingPlanForSampleLine,
  isAirGroupChecklistPlan,
} from '../workOrderSamplingPlanEnsure';
import { WorkOrderPdfPreviewDialog } from '../WorkOrderPdfPreviewDialog';

type ChecklistState = {
  bidang_pengujian: DynamicEntityRow | null;
  tujuan_pengambilan: DynamicEntityRow[];
  pengawetan: DynamicEntityRow[];
  larutan_penjerap: DynamicEntityRow[];
  peralatan_pengambilan_contoh: DynamicEntityRow[];
  peralatan_pengukuran_lapangan: DynamicEntityRow[];
  peralatan_pendukung: DynamicEntityRow[];
  peralatan_k3: DynamicEntityRow[];
  cara_pengambilan_contoh: DynamicEntityRow[];
  pengendalian_mutu_lapangan: DynamicEntityRow[];
  pengamanan_transportasi_contoh: DynamicEntityRow[];
  administrasi_contoh: DynamicEntityRow[];
  peta_sketsa_lokasi_files: File[];
  petugas_pengambil_contoh: StrapiManagedUserRow | null;
  disahkan_oleh_manajer_teknis: StrapiManagedUserRow | null;
  metode_sampling: DynamicEntityRow[];
  sni_methods: DynamicEntityRow[];
  jumlah_wadah_contoh_uji_air: string;
  jumlah_wadah_contoh_uji_padatan: string;
};

const emptyChecklist: ChecklistState = {
  bidang_pengujian: null,
  tujuan_pengambilan: [],
  pengawetan: [],
  larutan_penjerap: [],
  peralatan_pengambilan_contoh: [],
  peralatan_pengukuran_lapangan: [],
  peralatan_pendukung: [],
  peralatan_k3: [],
  cara_pengambilan_contoh: [],
  pengendalian_mutu_lapangan: [],
  pengamanan_transportasi_contoh: [],
  administrasi_contoh: [],
  peta_sketsa_lokasi_files: [],
  petugas_pengambil_contoh: null,
  disahkan_oleh_manajer_teknis: null,
  metode_sampling: [],
  sni_methods: [],
  jumlah_wadah_contoh_uji_air: '',
  jumlah_wadah_contoh_uji_padatan: '',
};

function rowString(row: DynamicEntityRow, key: string): string {
  return String(row[key] ?? '').trim();
}

function sniMethodOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return [rowString(row, 'kode_sni'), rowString(row, 'judul')].filter(Boolean).join(' — ') || row.documentId;
}

function referensiOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return [rowString(row, 'kode_kategori'), rowString(row, 'kode'), rowString(row, 'deskripsi1')]
    .filter(Boolean)
    .join(' — ') || row.documentId;
}

function userOptionLabel(user: StrapiManagedUserRow | null | undefined): string {
  if (!user) return '';
  return (user.username?.trim() ? user.username : user.email) || user.id;
}

function ChecklistUserSearchAutocomplete({
  label,
  userTypeCode,
  value,
  onChange,
  disabled,
  testId,
}: {
  label: string;
  userTypeCode: 'petugas_sampling' | 'manajer_teknis';
  value: StrapiManagedUserRow | null;
  onChange: (user: StrapiManagedUserRow | null) => void;
  disabled?: boolean;
  testId?: string;
}) {
  const { jwt } = useAuth();
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(typed.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const usersQ = useQuery({
    queryKey: ['strapi', 'users', 'checklist-search', jwt, userTypeCode, search],
    queryFn: () =>
      strapiListUsers(
        jwt!,
        { emailContains: '', searchContains: search, status: 'active', userTypeCode },
        search ? 20 : 3,
      ),
    enabled: Boolean(jwt),
  });
  const matches = usersQ.data?.rows ?? [];
  const options = useMemo(() => {
    if (!value || matches.some((row) => row.id === value.id)) return matches;
    return [value, ...matches];
  }, [matches, value]);

  return (
    <Autocomplete
      options={options}
      value={value}
      loading={usersQ.isFetching}
      disabled={disabled}
      getOptionLabel={userOptionLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opts) => {
        const ids = new Set(matches.map((row) => row.id));
        return opts.filter((row) => ids.has(row.id));
      }}
      noOptionsText="Tidak ada user"
      onInputChange={(_, next, reason) => {
        if (reason === 'input') {
          setTyped(next);
          return;
        }
        if (reason === 'clear') {
          setTyped('');
          setSearch('');
          onChange(null);
          return;
        }
        if (reason === 'reset' || reason === 'blur') {
          setTyped('');
          setSearch('');
        }
      }}
      onChange={(_, row) => onChange(row)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          fullWidth
          inputProps={{ ...params.inputProps, ...(testId ? { 'data-testid': testId } : {}) }}
        />
      )}
    />
  );
}

function relationMany(row: DynamicEntityRow | null | undefined, field: string): DynamicEntityRow[] {
  const raw = row?.[field];
  return Array.isArray(raw) ? (raw as DynamicEntityRow[]) : [];
}

function relationOne(row: DynamicEntityRow | null | undefined, field: string): DynamicEntityRow | null {
  const raw = row?.[field];
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as DynamicEntityRow) : null;
}

function relationConnect(row: DynamicEntityRow | null | undefined): { connect: string[] } | undefined {
  const documentId = String(row?.documentId ?? '').trim();
  return documentId ? { connect: [documentId] } : undefined;
}

function relationConnectMany(rows: DynamicEntityRow[]): { connect: string[] } | undefined {
  const ids = rows.map((row) => String(row.documentId ?? '').trim()).filter(Boolean);
  return ids.length ? { connect: ids } : undefined;
}

function trimToUndefined(v: string): string | undefined {
  const t = v.trim();
  return t || undefined;
}

function lineJenisPengukuranAsRow(line: StrapiSampleInformationRow): DynamicEntityRow | null {
  const jp = line.jenis_pengukuran;
  if (!jp?.documentId) return null;
  return { documentId: jp.documentId, nama_jenis: jp.nama_jenis ?? '', kode_jenis: jp.kode_jenis ?? '' };
}

function useReferensiOptionsQuery(
  jwt: string | null,
  queryKey: string,
  kodeKategori: string,
  mode: 'exact' | 'prefix' = 'exact',
  enabled = true,
) {
  return useQuery({
    queryKey: ['strapi', 'referensiOptions', jwt, queryKey, kodeKategori, mode],
    queryFn: async () => {
      const result = await strapiListDynamicEntityPage(jwt!, 'referensis', {
        populate: '*',
        page: 1,
        pageSize: 1000,
        ...(mode === 'exact'
          ? { exactFilters: { kode_kategori: kodeKategori } }
          : { containsFilters: { kode_kategori: kodeKategori } }),
      });
      return result.rows.sort((a, b) => referensiOptionLabel(a).localeCompare(referensiOptionLabel(b), 'id'));
    },
    enabled: Boolean(jwt) && enabled,
  });
}

function buildChecklistPayload(checklist: ChecklistState): Partial<StrapiSamplingPlanCreateData> {
  return {
    ...(relationConnect(checklist.bidang_pengujian) ? { bidang_pengujian: relationConnect(checklist.bidang_pengujian) } : {}),
    ...(relationConnectMany(checklist.tujuan_pengambilan) ? { tujuan_pengambilan: relationConnectMany(checklist.tujuan_pengambilan) } : {}),
    ...(relationConnectMany(checklist.pengawetan) ? { pengawetan: relationConnectMany(checklist.pengawetan) } : {}),
    ...(relationConnectMany(checklist.larutan_penjerap) ? { larutan_penjerap: relationConnectMany(checklist.larutan_penjerap) } : {}),
    ...(relationConnectMany(checklist.peralatan_pengambilan_contoh)
      ? { peralatan_pengambilan_contoh: relationConnectMany(checklist.peralatan_pengambilan_contoh) }
      : {}),
    ...(relationConnectMany(checklist.peralatan_pengukuran_lapangan)
      ? { peralatan_pengukuran_lapangan: relationConnectMany(checklist.peralatan_pengukuran_lapangan) }
      : {}),
    ...(relationConnectMany(checklist.peralatan_pendukung)
      ? { peralatan_pendukung: relationConnectMany(checklist.peralatan_pendukung) }
      : {}),
    ...(relationConnectMany(checklist.peralatan_k3) ? { peralatan_k3: relationConnectMany(checklist.peralatan_k3) } : {}),
    ...(relationConnectMany(checklist.cara_pengambilan_contoh)
      ? { cara_pengambilan_contoh: relationConnectMany(checklist.cara_pengambilan_contoh) }
      : {}),
    ...(relationConnectMany(checklist.pengendalian_mutu_lapangan)
      ? { pengendalian_mutu_lapangan: relationConnectMany(checklist.pengendalian_mutu_lapangan) }
      : {}),
    ...(relationConnectMany(checklist.pengamanan_transportasi_contoh)
      ? { pengamanan_transportasi_contoh: relationConnectMany(checklist.pengamanan_transportasi_contoh) }
      : {}),
    ...(relationConnectMany(checklist.administrasi_contoh)
      ? { administrasi_contoh: relationConnectMany(checklist.administrasi_contoh) }
      : {}),
    ...(checklist.petugas_pengambil_contoh
      ? { petugas_pengambil_contoh: strapiUserRelationConnect(checklist.petugas_pengambil_contoh) }
      : {}),
    ...(checklist.disahkan_oleh_manajer_teknis
      ? { disahkan_oleh_manajer_teknis: strapiUserRelationConnect(checklist.disahkan_oleh_manajer_teknis) }
      : {}),
    ...(relationConnectMany(checklist.metode_sampling) ? { metode_sampling: relationConnectMany(checklist.metode_sampling) } : {}),
    ...(relationConnectMany(checklist.sni_methods) ? { sni_methods: relationConnectMany(checklist.sni_methods) } : {}),
    ...(trimToUndefined(checklist.jumlah_wadah_contoh_uji_air)
      ? { jumlah_wadah_contoh_uji_air: trimToUndefined(checklist.jumlah_wadah_contoh_uji_air) }
      : {}),
    ...(trimToUndefined(checklist.jumlah_wadah_contoh_uji_padatan)
      ? { jumlah_wadah_contoh_uji_padatan: trimToUndefined(checklist.jumlah_wadah_contoh_uji_padatan) }
      : {}),
    ...(checklist.peta_sketsa_lokasi_files.length ? { peta_sketsa_lokasi_files: checklist.peta_sketsa_lokasi_files } : {}),
  };
}

function formatSurveyDateRange(startDate: string, finishDate: string): string {
  if (!startDate) return '—';
  if (!finishDate || startDate === finishDate) return scheduleDayTitle(startDate);
  return `${scheduleDayTitle(startDate)} — ${scheduleDayTitle(finishDate)}`;
}

export function WorkOrderSurveyTeamTab({
  workOrderDocumentId,
  samplingSchedule,
}: {
  workOrderDocumentId: string;
  samplingSchedule?: StrapiSamplingScheduleRow | null;
}) {
  const { jwt } = useAuth();
  const scheduleDocumentId = samplingSchedule?.documentId?.trim() ?? '';
  const scheduleNotes = samplingSchedule?.notes?.trim() ?? '';

  const surveyTeamsQ = useQuery({
    queryKey: ['strapi', 'surveyTeamsForWorkOrderResolved', jwt, workOrderDocumentId, scheduleDocumentId],
    queryFn: () =>
      strapiResolveSurveyTeamsForWorkOrder(jwt!, workOrderDocumentId, scheduleDocumentId || null),
    enabled: Boolean(jwt && workOrderDocumentId),
  });

  const displayEntries = useMemo(
    () => buildSurveyTeamDisplayEntries(surveyTeamsQ.data ?? []),
    [surveyTeamsQ.data],
  );

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: 960 }}>
      {surveyTeamsQ.isPending ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : surveyTeamsQ.isError ? (
        <Alert severity="error">
          {surveyTeamsQ.error instanceof Error ? surveyTeamsQ.error.message : 'Gagal memuat tim survey.'}
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Typography variant="subtitle2" sx={{ px: 1.5, py: 1, bgcolor: 'grey.100' }}>
              Petugas terpilih
            </Typography>
            {displayEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
                Belum ada petugas dari penjadwalan sampling.
              </Typography>
            ) : (
              <Table size="small" data-testid="work-order-survey-team-list">
                <TableHead>
                  <TableRow>
                    <TableCell>Petugas</TableCell>
                    <TableCell>Rentang tanggal</TableCell>
                    <TableCell>Jam</TableCell>
                    <TableCell>Lead</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayEntries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`work-order-survey-team-row-${entry.id}`}>
                      <TableCell>{entry.userName}</TableCell>
                      <TableCell>{formatSurveyDateRange(entry.startDate, entry.finishDate)}</TableCell>
                      <TableCell>{entry.time || '—'}</TableCell>
                      <TableCell>{entry.isLead ? 'Ya' : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>

          <TextField
            placeholder="Catatan penjadwalan..."
            value={scheduleNotes}
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            fullWidth
            disabled
            inputProps={{ 'data-testid': 'work-order-survey-team-notes' }}
          />
        </>
      )}
    </Stack>
  );
}

export type WorkOrderSamplingPlanChecklistBootstrap = {
  quotationDocumentId: string | null;
  planDocumentId: string | null;
  jenisLabels: string[];
  checklist: SamplingChecklistFormChecklist;
  options: SamplingChecklistFormOptions;
};

export type WorkOrderSamplingPlanTabHandle = {
  validate: () => string | null;
  save: () => Promise<void>;
  isBusy: () => boolean;
};

function checklistStateFromBootstrap(checklist: SamplingChecklistFormChecklist): ChecklistState {
  return {
    ...emptyChecklist,
    tujuan_pengambilan: checklist.tujuan_pengambilan,
    pengawetan: checklist.pengawetan,
    larutan_penjerap: checklist.larutan_penjerap,
    peralatan_pengambilan_contoh: checklist.peralatan_pengambilan_contoh,
    peralatan_pengukuran_lapangan: checklist.peralatan_pengukuran_lapangan,
    peralatan_pendukung: checklist.peralatan_pendukung,
    peralatan_k3: checklist.peralatan_k3,
    cara_pengambilan_contoh: checklist.cara_pengambilan_contoh,
    pengendalian_mutu_lapangan: checklist.pengendalian_mutu_lapangan,
    pengamanan_transportasi_contoh: checklist.pengamanan_transportasi_contoh,
    administrasi_contoh: checklist.administrasi_contoh,
    metode_sampling: checklist.metode_sampling,
    sni_methods: checklist.sni_methods,
    jumlah_wadah_contoh_uji_air: checklist.jumlah_wadah_contoh_uji_air,
    jumlah_wadah_contoh_uji_padatan: checklist.jumlah_wadah_contoh_uji_padatan,
    petugas_pengambil_contoh: checklist.petugas_pengambil_contoh,
    disahkan_oleh_manajer_teknis: checklist.disahkan_oleh_manajer_teknis,
  };
}

export const WorkOrderSamplingPlanTab = forwardRef<
  WorkOrderSamplingPlanTabHandle,
  {
    samples: StrapiSampleInformationRow[];
    workOrderDocumentId: string;
    quotationUsesGroups?: boolean;
    /** When set, only show sample lines whose jenis_pengukuran grouping.perencanaan_sampling matches. */
    airGroup?: SamplingPlanAirGroup;
    /**
     * `table` — daftar sampel + modal checklist (tab lama).
     * `inline_form` — form checklist langsung di tab (tab air / non-air).
     */
    layout?: 'table' | 'inline_form';
    /** Sembunyikan tombol simpan inline (mis. modal yang menyimpan via footer). */
    showSaveButton?: boolean;
    /** Jika true, tujuan/parameter/peralatan/cara/petugas selalu wajib (modal mulai survey). */
    requireCompleteChecklist?: boolean;
    /** Modal petugas di layar kecil: batasi item belum terpilih dan rapikan kolom cari. */
    checklistMobileCompact?: boolean;
    /**
     * Payload dari GET sampling-checklist-form. Kalau ada, tab tidak memanggil
     * endpoint master/plan sendiri (hanya jalur modal petugas sampling).
     */
    checklistBootstrap?: WorkOrderSamplingPlanChecklistBootstrap;
  }
>(function WorkOrderSamplingPlanTab(
  {
    samples,
    workOrderDocumentId,
    quotationUsesGroups = false,
    airGroup,
    layout = 'table',
    showSaveButton = true,
    requireCompleteChecklist = false,
    checklistMobileCompact = false,
    checklistBootstrap,
  },
  ref,
) {
  const usingBootstrap = checklistBootstrap != null;
  const { jwt } = useAuth();
  const sectionSearch = useChecklistSectionSearch(jwt);
  const qc = useQueryClient();
  const woDetailQ = useWorkOrderDetailQuery(jwt, usingBootstrap ? null : workOrderDocumentId);
  const [groupPlan, setGroupPlan] = useState<DynamicEntityRow | null>(() => {
    const planDocumentId = checklistBootstrap?.planDocumentId?.trim();
    return planDocumentId ? { documentId: planDocumentId } : null;
  });
  const [draft, setDraft] = useState<ChecklistState>(() =>
    checklistBootstrap ? checklistStateFromBootstrap(checklistBootstrap.checklist) : emptyChecklist,
  );
  const [creatingGroupPlan, setCreatingGroupPlan] = useState(false);
  const [ensurePlanError, setEnsurePlanError] = useState<string | null>(null);
  const ensureRequestedRef = useRef<string | null>(null);
  const checklistModalOpen = false;
  const formUiEnabled = layout === 'inline_form';
  const loadRemoteForm = formUiEnabled && !usingBootstrap;
  const parameterMetodaQ = useMasterEntityMiniListQuery(jwt, 'parameter-uji-metodas', loadRemoteForm);
  const sniMethodQ = useMasterEntityMiniListQuery(jwt, 'sni-methods', loadRemoteForm);
  const tujuanPengambilanQ = useReferensiOptionsQuery(jwt, 'tujuan_pengambilan', 'TUJUAN_SAMPLING', 'exact', loadRemoteForm);
  const pengawetanQ = useReferensiOptionsQuery(jwt, 'pengawetan', 'PENGAWETAN', 'exact', loadRemoteForm);
  const larutanPenjerapQ = useReferensiOptionsQuery(jwt, 'larutan_penjerap', 'LARUTAN_PENJERAP', 'exact', loadRemoteForm);
  const peralatanPengambilanContohQ = useReferensiOptionsQuery(jwt, 'peralatan_pengambilan_contoh', 'PERALATAN_SAMPLING', 'prefix', loadRemoteForm);
  const peralatanPengukuranLapanganQ = useReferensiOptionsQuery(jwt, 'peralatan_pengukuran_lapangan', 'PERALATAN_UKUR_LAP', 'exact', loadRemoteForm);
  const peralatanPendukungQ = useReferensiOptionsQuery(jwt, 'peralatan_pendukung', 'PERALATAN_PENDUKUNG', 'exact', loadRemoteForm);
  const peralatanK3Q = useReferensiOptionsQuery(jwt, 'peralatan_k3', 'PERALATAN_K3', 'exact', loadRemoteForm);
  const caraPengambilanContohQ = useReferensiOptionsQuery(jwt, 'cara_pengambilan_contoh', 'CARA_SAMPLING_', 'prefix', loadRemoteForm);
  const pengendalianMutuLapanganQ = useReferensiOptionsQuery(jwt, 'pengendalian_mutu_lapangan', 'PENGENDALIAN_MUTU', 'prefix', loadRemoteForm);
  const pengamananTransportasiContohQ = useReferensiOptionsQuery(jwt, 'pengamanan_transportasi_contoh', 'PENGAMANAN_SAMPLING', 'exact', loadRemoteForm);
  const administrasiContohQ = useReferensiOptionsQuery(jwt, 'administrasi_contoh', 'ADMINISTRASI_SAMPEL', 'exact', loadRemoteForm);
  const syncPlansQ = useQuery({
    queryKey: queryKeys.syncSamplingPlansForWorkOrder(jwt, workOrderDocumentId),
    queryFn: () => strapiSyncSamplingPlansForWorkOrder(jwt!, workOrderDocumentId),
    enabled: Boolean(jwt && workOrderDocumentId && !usingBootstrap),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const samplingPlansQ = useQuery({
    queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, workOrderDocumentId],
    queryFn: () => strapiListSamplingPlansForWorkOrder(jwt!, workOrderDocumentId),
    // Parent routes (SurveyDetailRoute) may already have plans cached; do not block on sync.
    enabled: Boolean(jwt && workOrderDocumentId && !usingBootstrap),
    staleTime: 0,
  });

  useEffect(() => {
    if (!syncPlansQ.isSuccess) return;
    void qc.invalidateQueries({
      queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, workOrderDocumentId],
    });
  }, [syncPlansQ.isSuccess, syncPlansQ.dataUpdatedAt, qc, jwt, workOrderDocumentId]);

  async function refreshAfterSamplingPlanChange() {
    await strapiSyncSamplingPlansForWorkOrder(jwt!, workOrderDocumentId);
    await qc.invalidateQueries({
      queryKey: queryKeys.syncSamplingPlansForWorkOrder(jwt, workOrderDocumentId),
    });
    await samplingPlansQ.refetch();
    await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, workOrderDocumentId) });
    await qc.invalidateQueries({ queryKey: queryKeys.surveyRowsPrefix(jwt) });
    await qc.invalidateQueries({
      queryKey: queryKeys.surveyRowsForWorkOrder(jwt, workOrderDocumentId),
    });
  }
  const visibleSamples = useMemo(() => {
    if (usingBootstrap) return [];
    let rows: StrapiSampleInformationRow[];
    if (!quotationUsesGroups) {
      rows = samples;
    } else if (samplingPlansQ.isPending && samplingPlansQ.data === undefined) {
      rows = [];
    } else if (samplingPlansQ.isError) {
      rows = [];
    } else {
      rows = filterSamplesForWorkOrderSamplingPlan(
        samples,
        (samplingPlansQ.data ?? []).filter((p) => !isAirGroupChecklistPlan(p)),
        true,
      );
    }
    return airGroup ? filterSamplesByAirGroup(rows, airGroup) : rows;
  }, [
    usingBootstrap,
    samples,
    quotationUsesGroups,
    airGroup,
    samplingPlansQ.data,
    samplingPlansQ.isPending,
    samplingPlansQ.isError,
  ]);
  const parameterUjiDocumentIds = useMemo(
    () => parameterDocumentIdsFromSampleLines(visibleSamples),
    [visibleSamples],
  );
  const parameterChecklistMappingQ = useQuery({
    queryKey: ['strapi', 'peralatan-to-parameter-ujis', jwt, parameterUjiDocumentIds.join(',')],
    queryFn: () => strapiListPeralatanToParameterUjiByParameterIds(jwt!, parameterUjiDocumentIds),
    enabled: loadRemoteForm && Boolean(jwt) && parameterUjiDocumentIds.length > 0,
  });

  const updateMut = useMutation({
    mutationFn: ({ documentId, data }: { documentId: string; data: Partial<StrapiSamplingPlanCreateData> }) =>
      strapiUpdateSamplingPlanChecklist(jwt!, documentId, data),
    onSuccess: async () => {
      if (usingBootstrap) return;
      await refreshAfterSamplingPlanChange();
    },
  });
  const ensureGroupPlanMut = useMutation({
    mutationFn: async (group: SamplingPlanAirGroup) => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      const plans = samplingPlansQ.data ?? [];
      const existing = findSamplingPlanForAirGroup(plans, group);
      if (existing?.documentId) return existing;

      const quotationDocumentId = String(woDetailQ.data?.quotation?.documentId ?? '').trim() || null;
      await strapiCreateSamplingPlan(
        jwt,
        buildSamplingPlanCreateDataForAirGroup(workOrderDocumentId, quotationDocumentId, group),
      );
      const refreshed = await samplingPlansQ.refetch();
      const plan = findSamplingPlanForAirGroup(refreshed.data ?? [], group);
      if (!plan?.documentId) {
        throw new Error('Sampling plan group dibuat tetapi tidak dapat dimuat. Muat ulang halaman lalu coba lagi.');
      }
      return plan;
    },
    onSuccess: (plan) => {
      setEnsurePlanError(null);
      setGroupPlan(plan);
      void refreshAfterSamplingPlanChange();
    },
    onError: (error) => {
      setEnsurePlanError(error instanceof Error ? error.message : 'Gagal menyiapkan sampling plan group.');
    },
  });
  const metodeSamplingOptions = useMemo(
    () =>
      [...(checklistBootstrap?.options.metode_sampling ?? parameterMetodaQ.data ?? [])].sort((a, b) =>
        metodaSamplingPlainLabel(a).localeCompare(metodaSamplingPlainLabel(b), 'id'),
      ),
    [checklistBootstrap?.options.metode_sampling, parameterMetodaQ.data],
  );
  const sniMethodOptions = useMemo(
    () =>
      [...(checklistBootstrap?.options.sni_methods ?? sniMethodQ.data ?? [])].sort((a, b) =>
        sniMethodOptionLabel(a).localeCompare(sniMethodOptionLabel(b), 'id'),
      ),
    [checklistBootstrap?.options.sni_methods, sniMethodQ.data],
  );
  const tujuanOptions = checklistBootstrap?.options.tujuan_pengambilan ?? tujuanPengambilanQ.data ?? [];
  const pengawetanOptions = checklistBootstrap?.options.pengawetan ?? pengawetanQ.data ?? [];
  const larutanOptions = checklistBootstrap?.options.larutan_penjerap ?? larutanPenjerapQ.data ?? [];
  const peralatanPengambilanOptions =
    checklistBootstrap?.options.peralatan_pengambilan_contoh ?? peralatanPengambilanContohQ.data ?? [];
  const peralatanUkurOptions =
    checklistBootstrap?.options.peralatan_pengukuran_lapangan ?? peralatanPengukuranLapanganQ.data ?? [];
  const peralatanPendukungOptions = checklistBootstrap?.options.peralatan_pendukung ?? peralatanPendukungQ.data ?? [];
  const peralatanK3Options = checklistBootstrap?.options.peralatan_k3 ?? peralatanK3Q.data ?? [];
  const caraOptions = checklistBootstrap?.options.cara_pengambilan_contoh ?? caraPengambilanContohQ.data ?? [];
  const mutuOptions = checklistBootstrap?.options.pengendalian_mutu_lapangan ?? pengendalianMutuLapanganQ.data ?? [];
  const transportOptions =
    checklistBootstrap?.options.pengamanan_transportasi_contoh ?? pengamananTransportasiContohQ.data ?? [];
  const administrasiOptions = checklistBootstrap?.options.administrasi_contoh ?? administrasiContohQ.data ?? [];
  const optionsLoading = !usingBootstrap;

  function userFromPlan(plan: DynamicEntityRow | null, field: string): StrapiManagedUserRow | null {
    const raw = relationOne(plan, field);
    if (!raw) return null;
    const documentId = String(raw.documentId ?? '').trim();
    const idRaw = raw.id;
    const numericId = idRaw != null && Number.isFinite(Number(idRaw)) ? Number(idRaw) : null;
    return {
      id: numericId != null ? String(numericId) : documentId || 'u:unknown',
      numericId,
      documentId: documentId || null,
      username: String(raw.username ?? ''),
      email: String(raw.email ?? ''),
      blocked: false,
      userTypeCode: null,
      userTypeName: null,
    };
  }

  function checklistFromGroupPlan(plan: DynamicEntityRow | null): ChecklistState {
    return {
      ...emptyChecklist,
      bidang_pengujian: null,
      tujuan_pengambilan: relationMany(plan, 'tujuan_pengambilan'),
      pengawetan: relationMany(plan, 'pengawetan'),
      larutan_penjerap: relationMany(plan, 'larutan_penjerap'),
      peralatan_pengambilan_contoh: relationMany(plan, 'peralatan_pengambilan_contoh'),
      peralatan_pengukuran_lapangan: relationMany(plan, 'peralatan_pengukuran_lapangan'),
      peralatan_pendukung: relationMany(plan, 'peralatan_pendukung'),
      peralatan_k3: relationMany(plan, 'peralatan_k3'),
      cara_pengambilan_contoh: relationMany(plan, 'cara_pengambilan_contoh'),
      pengendalian_mutu_lapangan: relationMany(plan, 'pengendalian_mutu_lapangan'),
      pengamanan_transportasi_contoh: relationMany(plan, 'pengamanan_transportasi_contoh'),
      administrasi_contoh: relationMany(plan, 'administrasi_contoh'),
      petugas_pengambil_contoh: userFromPlan(plan, 'petugas_pengambil_contoh'),
      disahkan_oleh_manajer_teknis: userFromPlan(plan, 'disahkan_oleh_manajer_teknis'),
      metode_sampling: relationMany(plan, 'metode_sampling'),
      sni_methods: relationMany(plan, 'sni_methods'),
      jumlah_wadah_contoh_uji_air: String(plan?.jumlah_wadah_contoh_uji_air ?? ''),
      jumlah_wadah_contoh_uji_padatan: String(plan?.jumlah_wadah_contoh_uji_padatan ?? ''),
      peta_sketsa_lokasi_files: [],
    };
  }

  function checklistHasData(state: ChecklistState): boolean {
    return (
      state.tujuan_pengambilan.length > 0 ||
      state.peralatan_pengambilan_contoh.length > 0 ||
      state.peralatan_pengukuran_lapangan.length > 0 ||
      state.cara_pengambilan_contoh.length > 0 ||
      Boolean(state.petugas_pengambil_contoh) ||
      Boolean(state.jumlah_wadah_contoh_uji_air.trim()) ||
      Boolean(state.jumlah_wadah_contoh_uji_padatan.trim())
    );
  }

  function validateMandatoryChecklist(state: ChecklistState): string | null {
    const missing: string[] = [];
    if (state.tujuan_pengambilan.length === 0) missing.push('Tujuan pengambilan contoh');
    if (state.metode_sampling.length === 0) missing.push('Parameter');
    const hasPeralatan =
      state.peralatan_pengambilan_contoh.length > 0 ||
      state.peralatan_pengukuran_lapangan.length > 0 ||
      state.peralatan_pendukung.length > 0 ||
      state.peralatan_k3.length > 0;
    if (!hasPeralatan) missing.push('Peralatan (minimal satu jenis)');
    if (state.cara_pengambilan_contoh.length === 0) missing.push('Cara pengambilan contoh');
    if (!state.petugas_pengambil_contoh) missing.push('Petugas pengambil contoh');
    if (missing.length === 0) return null;
    const groupLabel = airGroup ? samplingPlanTabLabel(airGroup) : 'Rencana sampling';
    return `${groupLabel}: lengkapi ${missing.join(', ')}.`;
  }

  /** WO lama: checklist masih di plan per baris — pakai sebagai sumber draft sampai user simpan ke plan group. */
  function hydrateChecklistFromLegacyLinePlans(
    groupPlan: DynamicEntityRow | null,
    plans: DynamicEntityRow[],
  ): ChecklistState {
    const fromGroup = checklistFromGroupPlan(groupPlan);
    if (checklistHasData(fromGroup)) return fromGroup;
    for (let i = 0; i < visibleSamples.length; i++) {
      const line = visibleSamples[i]!;
      const lineDocId = line.documentId?.trim() ?? '';
      const originalIdx = lineDocId
        ? samples.findIndex((s) => (s.documentId?.trim() ?? '') === lineDocId)
        : i;
      const linePlan = findSamplingPlanForSampleLine(line, originalIdx >= 0 ? originalIdx : i, plans);
      const fromLine = checklistFromGroupPlan(linePlan);
      if (checklistHasData(fromLine)) return fromLine;
    }
    return fromGroup;
  }

  useEffect(() => {
    if (usingBootstrap) return;
    if (layout !== 'inline_form' || !airGroup) {
      setGroupPlan(null);
      return;
    }
    if (visibleSamples.length === 0) {
      setGroupPlan(null);
      return;
    }
    if (samplingPlansQ.isPending && samplingPlansQ.data === undefined) return;
    if (samplingPlansQ.isError) return;

    const existing = findSamplingPlanForAirGroup(samplingPlansQ.data ?? [], airGroup);
    if (existing?.documentId) {
      if (existing.documentId !== groupPlan?.documentId) {
        setGroupPlan(existing);
        const hydrated = hydrateChecklistFromLegacyLinePlans(existing, samplingPlansQ.data ?? []);
        const mappings = (parameterChecklistMappingQ.data ?? []).filter((row) =>
          mappingAppliesToParameterIds(row, parameterUjiDocumentIds),
        );
        setDraft(applyParameterUjiMappingsToEmptySections(hydrated, mappings));
      }
      ensureRequestedRef.current = null;
      return;
    }

    if (ensureGroupPlanMut.isPending) return;

    if (ensureRequestedRef.current === airGroup || ensurePlanError) return;
    ensureRequestedRef.current = airGroup;
    ensureGroupPlanMut.mutate(airGroup);
  }, [
    layout,
    airGroup,
    visibleSamples.length,
    groupPlan?.documentId,
    ensurePlanError,
    ensureGroupPlanMut,
    ensureGroupPlanMut.isPending,
    samplingPlansQ.data,
    samplingPlansQ.isPending,
    samplingPlansQ.isError,
    usingBootstrap,
  ]);

  const parameterMappingApplyKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (usingBootstrap || !formUiEnabled || !groupPlan?.documentId || !parameterChecklistMappingQ.isSuccess) return;
    const key = `${groupPlan.documentId}|${parameterUjiDocumentIds.join(',')}`;
    if (parameterMappingApplyKeyRef.current === key) return;
    parameterMappingApplyKeyRef.current = key;
    const mappings = (parameterChecklistMappingQ.data ?? []).filter((row) =>
      mappingAppliesToParameterIds(row, parameterUjiDocumentIds),
    );
    if (!mappings.length) return;
    setDraft((prev) => applyParameterUjiMappingsToEmptySections(prev, mappings));
  }, [
    formUiEnabled,
    usingBootstrap,
    groupPlan,
    parameterChecklistMappingQ.isSuccess,
    parameterChecklistMappingQ.data,
    parameterUjiDocumentIds,
  ]);

  async function saveChecklist() {
    if (layout !== 'inline_form' || !airGroup) return;
    let plan = groupPlan ?? (usingBootstrap ? null : findSamplingPlanForAirGroup(samplingPlansQ.data ?? [], airGroup));
    if (usingBootstrap) {
      const existingId = String(checklistBootstrap?.planDocumentId ?? plan?.documentId ?? '').trim();
      if (!existingId) {
        if (!jwt) throw new Error('Sesi tidak valid.');
        setCreatingGroupPlan(true);
        try {
          const created = await strapiCreateSamplingPlan(
            jwt,
            buildSamplingPlanCreateDataForAirGroup(
              workOrderDocumentId,
              checklistBootstrap?.quotationDocumentId ?? null,
              airGroup,
            ),
          );
          const createdId = String(created.documentId ?? '').trim();
          if (!createdId) {
            throw new Error('Sampling plan group dibuat tetapi tidak dapat dimuat. Muat ulang halaman lalu coba lagi.');
          }
          plan = created;
          setGroupPlan(created);
        } finally {
          setCreatingGroupPlan(false);
        }
      } else if (!plan?.documentId) {
        plan = { documentId: existingId };
        setGroupPlan(plan);
      }
    } else if (!plan?.documentId) {
      plan = await ensureGroupPlanMut.mutateAsync(airGroup);
    }
    if (!plan?.documentId) return;
    const payload = buildChecklistPayload({ ...draft, bidang_pengujian: null });
    const { bidang_pengujian: _omit, ...sharedPayload } = payload;
    await updateMut.mutateAsync({
      documentId: String(plan.documentId),
      data: sharedPayload,
    });
    setGroupPlan(plan);
  }

  function renderChecklistFields(disabled: boolean) {
    const groupKey = airGroup ?? 'shared';
    return (
      <>
        <Stack spacing={2} sx={{ width: '100%' }} data-testid={`sampling-plan-checklist-panel-${groupKey}`}>
          <MasterMultiCheckboxGroup label="Tujuan pengambilan contoh" options={tujuanOptions} value={draft.tujuan_pengambilan} onChange={(rows) => setDraft((prev) => ({ ...prev, tujuan_pengambilan: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && tujuanPengambilanQ.isPending} onSearchMiss={sectionSearch?.tujuan} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-tujuan`} />
          <Alert severity="info">
            Jenis di group ini: <strong>{groupJenisLabels.join(', ') || '—'}</strong>
          </Alert>
          <MasterMultiCheckboxGroup
            label="Metode sampling"
            options={sniMethodOptions}
            value={draft.sni_methods}
            onChange={(rows) => setDraft((prev) => ({ ...prev, sni_methods: rows }))}
            getOptionLabel={sniMethodOptionLabel}
            loading={optionsLoading && sniMethodQ.isPending}
            disabled={disabled}
            onSearchMiss={sectionSearch?.sni}
            unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-sni`}
          />
          <MasterMultiCheckboxGroup
            label="Parameter"
            options={metodeSamplingOptions}
            value={draft.metode_sampling}
            onChange={(rows) => setDraft((prev) => ({ ...prev, metode_sampling: rows }))}
            getOptionLabel={metodaSamplingPlainLabel}
            renderOptionLabel={(row) => <MetodaSamplingChemLabel row={row} />}
            loading={optionsLoading && parameterMetodaQ.isPending}
            disabled={disabled}
            onSearchMiss={sectionSearch?.parameter}
            unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-metode`}
          />
          <MasterMultiCheckboxGroup label="Larutan penyerap" options={larutanOptions} value={draft.larutan_penjerap} onChange={(rows) => setDraft((prev) => ({ ...prev, larutan_penjerap: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && larutanPenjerapQ.isPending} onSearchMiss={sectionSearch?.larutan} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-larutan`} />
          <MasterMultiCheckboxGroup label="Peralatan pengambil contoh" options={peralatanPengambilanOptions} value={draft.peralatan_pengambilan_contoh} onChange={(rows) => setDraft((prev) => ({ ...prev, peralatan_pengambilan_contoh: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && peralatanPengambilanContohQ.isPending} onSearchMiss={sectionSearch?.peralatanPengambilan} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-peralatan-pengambilan`} />
          <MasterMultiCheckboxGroup label="Peralatan pengukuran lapangan" options={peralatanUkurOptions} value={draft.peralatan_pengukuran_lapangan} onChange={(rows) => setDraft((prev) => ({ ...prev, peralatan_pengukuran_lapangan: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && peralatanPengukuranLapanganQ.isPending} onSearchMiss={sectionSearch?.peralatanUkur} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-peralatan-ukur`} />
          <MasterMultiCheckboxGroup label="Peralatan pendukung" options={peralatanPendukungOptions} value={draft.peralatan_pendukung} onChange={(rows) => setDraft((prev) => ({ ...prev, peralatan_pendukung: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && peralatanPendukungQ.isPending} onSearchMiss={sectionSearch?.peralatanPendukung} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-peralatan-pendukung`} />
          <MasterMultiCheckboxGroup label="Peralatan K-3" options={peralatanK3Options} value={draft.peralatan_k3} onChange={(rows) => setDraft((prev) => ({ ...prev, peralatan_k3: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && peralatanK3Q.isPending} onSearchMiss={sectionSearch?.peralatanK3} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-peralatan-k3`} />
          <MasterMultiCheckboxGroup label="Cara pengambilan contoh" options={filterCaraSamplingReferensiByAirGroup(caraOptions, airGroup ?? 'non_air')} value={draft.cara_pengambilan_contoh} onChange={(rows) => setDraft((prev) => ({ ...prev, cara_pengambilan_contoh: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && caraPengambilanContohQ.isPending} onSearchMiss={sectionSearch ? async (query) => filterCaraSamplingReferensiByAirGroup(await sectionSearch.cara(query), airGroup ?? 'non_air') : undefined} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-cara`} />
          <MasterMultiCheckboxGroup label="Pengendalian mutu lapangan" options={mutuOptions} value={draft.pengendalian_mutu_lapangan} onChange={(rows) => setDraft((prev) => ({ ...prev, pengendalian_mutu_lapangan: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && pengendalianMutuLapanganQ.isPending} onSearchMiss={sectionSearch?.mutu} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-mutu`} />
          <MasterMultiCheckboxGroup label="Pengamanan dan transportasi contoh" options={transportOptions} value={draft.pengamanan_transportasi_contoh} onChange={(rows) => setDraft((prev) => ({ ...prev, pengamanan_transportasi_contoh: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && pengamananTransportasiContohQ.isPending} onSearchMiss={sectionSearch?.transport} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-transport`} />
          <MasterMultiCheckboxGroup label="Administrasi contoh" options={administrasiOptions} value={draft.administrasi_contoh} onChange={(rows) => setDraft((prev) => ({ ...prev, administrasi_contoh: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && administrasiContohQ.isPending} onSearchMiss={sectionSearch?.administrasi} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-administrasi`} />
          <MasterMultiCheckboxGroup label="Pengawetan (air)" options={pengawetanOptions} value={draft.pengawetan} onChange={(rows) => setDraft((prev) => ({ ...prev, pengawetan: rows }))} getOptionLabel={referensiOptionLabel} loading={optionsLoading && pengawetanQ.isPending} onSearchMiss={sectionSearch?.pengawetan} unselectedVisibleLimit={checklistMobileCompact ? 2 : undefined} compactSearch={checklistMobileCompact} data-testid={`sampling-plan-checklist-${groupKey}-pengawetan`} />
          <ChecklistUserSearchAutocomplete
            label="Created by petugas pengambil contoh"
            userTypeCode="petugas_sampling"
            value={draft.petugas_pengambil_contoh}
            onChange={(row) => setDraft((prev) => ({ ...prev, petugas_pengambil_contoh: row }))}
            disabled={disabled}
            testId={`sampling-plan-checklist-${groupKey}-petugas`}
          />
          <ChecklistUserSearchAutocomplete
            label="Disahkan oleh manajer teknis"
            userTypeCode="manajer_teknis"
            value={draft.disahkan_oleh_manajer_teknis}
            onChange={(row) => setDraft((prev) => ({ ...prev, disahkan_oleh_manajer_teknis: row }))}
            disabled={disabled}
          />
          <TextField label="Jumlah wadah contoh uji air" size="small" value={draft.jumlah_wadah_contoh_uji_air} onChange={(ev) => setDraft((prev) => ({ ...prev, jumlah_wadah_contoh_uji_air: ev.target.value }))} fullWidth disabled={disabled} />
          <TextField label="Jumlah wadah contoh uji padatan" size="small" value={draft.jumlah_wadah_contoh_uji_padatan} onChange={(ev) => setDraft((prev) => ({ ...prev, jumlah_wadah_contoh_uji_padatan: ev.target.value }))} fullWidth disabled={disabled} />
        </Stack>
      </>
    );
  }

  const formBusy = usingBootstrap
    ? false
    : !groupPlan?.documentId &&
      (ensureGroupPlanMut.isPending || (samplingPlansQ.isPending && samplingPlansQ.data === undefined));
  const formBlocked = usingBootstrap
    ? false
    : Boolean(ensurePlanError) || (!groupPlan?.documentId && !formBusy);
  const groupJenisLabels = usingBootstrap
    ? checklistBootstrap.jenisLabels
    : visibleSamples.map((line) => formatJenisPengukuranLine(line.jenis_pengukuran)).filter(Boolean);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        if (layout !== 'inline_form' || !airGroup) return null;
        if (!usingBootstrap && visibleSamples.length === 0) return null;
        if (formBusy || formBlocked || ensurePlanError) {
          return `${samplingPlanTabLabel(airGroup)}: form belum siap. Tunggu sebentar atau muat ulang halaman.`;
        }
        // Modal mulai survey: selalu wajib. Tab WO biasa: wajib hanya jika sudah mulai diisi.
        if (requireCompleteChecklist || checklistHasData(draft)) {
          return validateMandatoryChecklist(draft);
        }
        return null;
      },
      save: async () => {
        await saveChecklist();
      },
      isBusy: () =>
        usingBootstrap
          ? updateMut.isPending || creatingGroupPlan
          : formBusy ||
            updateMut.isPending ||
            ensureGroupPlanMut.isPending ||
            samplingPlansQ.isPending ||
            parameterChecklistMappingQ.isLoading,
    }),
    [
      layout,
      airGroup,
      usingBootstrap,
      creatingGroupPlan,
      visibleSamples.length,
      formBusy,
      formBlocked,
      ensurePlanError,
      requireCompleteChecklist,
      draft,
      groupPlan,
      samplingPlansQ.data,
      samplingPlansQ.isPending,
      updateMut.isPending,
      ensureGroupPlanMut.isPending,
      parameterChecklistMappingQ.isLoading,
      checklistBootstrap,
      jwt,
      workOrderDocumentId,
    ],
  );

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {layout === 'table' ? (
        <>
          <Typography variant="body2" color="text.secondary">
            {quotationUsesGroups
              ? 'Hanya group sample yang sudah dijadwalkan dan terhubung ke work order ini yang ditampilkan.'
              : 'Daftar jenis pengukuran pada work order. Checklist peralatan diisi di tab air / udara sesuai grouping.'}
          </Typography>
          {quotationUsesGroups && samplingPlansQ.isPending && samplingPlansQ.data === undefined ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : null}
          {quotationUsesGroups && (syncPlansQ.isError || samplingPlansQ.isError) ? (
            <Alert severity="error">
              {(syncPlansQ.error ?? samplingPlansQ.error) instanceof Error
                ? (syncPlansQ.error ?? samplingPlansQ.error)?.message
                : 'Gagal memuat rencana sampling work order.'}
            </Alert>
          ) : null}
          {!samplingPlansQ.isPending && visibleSamples.length === 0 ? (
            <Alert severity="warning">
              {quotationUsesGroups
                ? 'Belum ada group sample terjadwal pada work order ini.'
                : 'Work order ini belum memiliki baris sampel quotation.'}
            </Alert>
          ) : null}
          {!samplingPlansQ.isPending && visibleSamples.length > 0 ? (
            <Table
              size="small"
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-root': { verticalAlign: 'middle', py: 1 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56 }}>#</TableCell>
                  <TableCell sx={{ width: '22%' }}>Jenis pengukuran</TableCell>
                  <TableCell sx={{ width: '24%' }}>Baku mutu</TableCell>
                  <TableCell>Parameter uji</TableCell>
                  <TableCell align="right" sx={{ width: 112 }}>
                    Jumlah titik
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleSamples.map((line, idx) => (
                  <TableRow key={line.documentId || `line-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{formatJenisPengukuranLine(line.jenis_pengukuran)}</TableCell>
                    <TableCell>{formatBakuMutuLine(line.baku_mutu)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" component="div">
                        <ChemParameterUjiCell raw={line.parameter_uji} items={line.parameter_ujis} puLookup={new Map()} />
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {line.sample_point_count != null ? String(line.sample_point_count) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            Satu form checklist peralatan untuk seluruh jenis pengukuran group{' '}
            <strong>{airGroup === 'air' ? 'air dan padatan' : 'udara ambien, emisi dan gangguan lingkungan'}</strong>.
            Disimpan sebagai satu sampling plan per group.
          </Typography>
          {!usingBootstrap && quotationUsesGroups && samplingPlansQ.isPending && samplingPlansQ.data === undefined ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : !usingBootstrap && visibleSamples.length === 0 ? (
            <Alert severity="warning">Tidak ada jenis pengukuran untuk group ini pada work order.</Alert>
          ) : null}
        </>
      )}

      {formUiEnabled && !usingBootstrap && samplingPlansQ.isError ? (
        <Alert severity="error">
          {samplingPlansQ.error instanceof Error ? samplingPlansQ.error.message : 'Gagal memuat checklist sampling plan.'}
        </Alert>
      ) : null}
      {updateMut.isError ? (
        <Alert severity="error">
          {updateMut.error instanceof Error ? updateMut.error.message : 'Gagal menyimpan checklist sampling plan.'}
        </Alert>
      ) : null}
      {updateMut.isSuccess && layout === 'inline_form' ? (
        <Alert severity="success" onClose={() => updateMut.reset()}>
          Checklist group tersimpan.
        </Alert>
      ) : null}

      {layout === 'inline_form' && (usingBootstrap || visibleSamples.length > 0) ? (
        <Stack spacing={2}>
          {formBusy ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={3} gap={1}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Menyiapkan sampling plan group…
              </Typography>
            </Box>
          ) : ensurePlanError ? (
            <Stack spacing={1.5}>
              <Alert severity="error">{ensurePlanError}</Alert>
              <Button
                variant="outlined"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                disabled={ensureGroupPlanMut.isPending || !airGroup}
                onClick={() => {
                  if (!airGroup) return;
                  ensureRequestedRef.current = null;
                  setEnsurePlanError(null);
                  ensureGroupPlanMut.reset();
                  ensureGroupPlanMut.mutate(airGroup);
                }}
              >
                Coba lagi
              </Button>
            </Stack>
          ) : formBlocked ? (
            <Alert severity="error">
              Sampling plan group belum tersedia. Muat ulang halaman lalu coba lagi.
            </Alert>
          ) : (
            <>
              {parameterChecklistMappingQ.isLoading ? (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  data-testid="sampling-plan-parameter-mapping-loading"
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Memuat mapping peralatan × parameter uji…
                  </Typography>
                </Box>
              ) : null}
              {renderChecklistFields(false)}
              {showSaveButton ? (
                <Box>
                  <Button
                    type="button"
                    variant="contained"
                    disabled={
                      updateMut.isPending ||
                      creatingGroupPlan ||
                      (!usingBootstrap &&
                        (ensureGroupPlanMut.isPending ||
                          samplingPlansQ.isPending ||
                          parameterChecklistMappingQ.isLoading))
                    }
                    onClick={() => void saveChecklist()}
                  >
                    {updateMut.isPending ? 'Menyimpan…' : 'Simpan checklist'}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
});

export function WorkOrderDetailRoute() {
  const { jwt, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId: rawId } = useParams<{ documentId: string }>();
  const documentId = rawId ? decodeURIComponent(rawId) : null;
  const allowEditWorkOrder = canEditWorkOrder(user);
  const menuQ = useNavigationMenuQuery(jwt);
  const showCustomPdfButton = isSuperadminUser(menuQ.data?.access_codes);
  const [pdfOpen, setPdfOpen] = useState(false);

  const [statusAdvanceMessage, setStatusAdvanceMessage] = useState<string | null>(() => {
    const state = location.state as { statusAdvanceMessage?: string } | null;
    return state?.statusAdvanceMessage ?? null;
  });

  useEffect(() => {
    const state = location.state as { statusAdvanceMessage?: string } | null;
    if (!state?.statusAdvanceMessage) return;
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} },
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  const detailQ = useWorkOrderDetailQuery(jwt, documentId);

  const { tab: activeTab } = useMemo(
    () => parseWorkOrderRouteHash(location.hash),
    [location.hash],
  );

  useDocumentTitle(appDocumentTitle('Detail work order'));

  const samples = detailQ.data?.sample_informations ?? [];
  const hasAirSamples = samplesHaveAirGroup(samples, 'air');
  const hasNonAirSamples = samplesHaveAirGroup(samples, 'non_air');
  const resolvedSamplingPlanTab = resolveSamplingPlanRouteTab(activeTab, {
    hasAir: hasAirSamples,
    hasNonAir: hasNonAirSamples,
  });

  function goHash(nextTab: WorkOrderRouteTab, nextSampleId?: string | null) {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: buildWorkOrderRouteHash(nextTab, nextSampleId),
      },
      { replace: true },
    );
  }

  useEffect(() => {
    if (!detailQ.data) return;
    if (activeTab !== 'sampling_plan_air' && activeTab !== 'sampling_plan_non_air') {
      return;
    }
    const resolved = resolveSamplingPlanRouteTab(activeTab, {
      hasAir: samplesHaveAirGroup(detailQ.data.sample_informations ?? [], 'air'),
      hasNonAir: samplesHaveAirGroup(detailQ.data.sample_informations ?? [], 'non_air'),
    });
    if (resolved && resolved !== activeTab) {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: buildWorkOrderRouteHash(resolved, null),
        },
        { replace: true },
      );
    }
  }, [activeTab, detailQ.data, location.pathname, location.search, navigate]);

  if (!documentId) {
    return (
      <PageShell title="Detail work order">
        <Alert severity="warning">ID work order tidak valid.</Alert>
        <Button component={RouterLink} to="/sampling/work-orders" sx={{ mt: 2 }}>
          Kembali
        </Button>
      </PageShell>
    );
  }

  if (detailQ.isPending) {
    return (
      <PageShell fill title="Detail work order">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      </PageShell>
    );
  }

  if (detailQ.isError || !detailQ.data) {
    return (
      <PageShell title="Detail work order">
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat work order.'}
        </Alert>
        <Button component={RouterLink} to="/sampling/work-orders">
          Kembali
        </Button>
      </PageShell>
    );
  }

  const wo = detailQ.data;
  const viewActiveTab: WorkOrderRouteTab =
    resolvedSamplingPlanTab ??
    (activeTab === 'survey_team' ? 'survey_team' : 'detail');

  return (
    <PageShell
      fill
      title="Detail work order"
      extras={
        <Tabs
          value={viewActiveTab}
          onChange={(_, value: WorkOrderRouteTab) => {
            if (value === 'detail') {
              goHash('detail');
            } else if (value === 'survey_team') {
              goHash('survey_team');
            } else {
              goHash(value, null);
            }
          }}
          sx={PAGE_SHELL_SCROLLABLE_TABS_SX}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="Bagian detail work order"
        >
          <Tab label="Detail" value="detail" data-testid="work-order-view-tab-detail" />
          <Tab
            label="Daftar Sample"
            value="sampling_plan"
            data-testid="work-order-view-tab-sampling-plan"
          />
          {hasAirSamples ? (
            <Tab
              label={samplingPlanTabLabel('air')}
              value="sampling_plan_air"
              data-testid="work-order-view-tab-sampling-plan-air"
            />
          ) : null}
          {hasNonAirSamples ? (
            <Tab
              label={samplingPlanTabLabel('non_air')}
              value="sampling_plan_non_air"
              data-testid="work-order-view-tab-sampling-plan-non-air"
            />
          ) : null}
          <Tab label="Tim Survey" value="survey_team" data-testid="work-order-view-tab-survey-team" />
        </Tabs>
      }
      actions={
        <WorkOrderDetailViewFooterActions
          documentId={documentId}
          canEdit={allowEditWorkOrder}
          onPreviewPdf={() => setPdfOpen(true)}
        />
      }
    >
      <CollapsibleKeteranganSplit
        toggleTestId="work-order-keterangan-toggle"
        keterangan={
          <Stack spacing={1}>
            <WorkOrderDetailIntroBlurb wo={wo} />
            {statusAdvanceMessage ? (
              <Alert severity="success" onClose={() => setStatusAdvanceMessage(null)}>
                {statusAdvanceMessage}
              </Alert>
            ) : null}
          </Stack>
        }
      >
        {viewActiveTab === 'detail' && (
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            {samples.length > 0 && !hasAirSamples && !hasNonAirSamples ? (
              <Alert severity="info">
                Tab rencana air/udara belum muncul karena jenis pengukuran belum punya flag{' '}
                <strong>grouping.perencanaan_sampling</strong> di master (1=air, 2=udara, 3=LB3). Tab
                «Daftar Sample» tetap bisa dipakai.
              </Alert>
            ) : null}
            <WorkOrderDetailScheduleReadonlyFields wo={wo} />
          </Stack>
        )}
        {viewActiveTab === 'sampling_plan' && (
          <WorkOrderSamplingPlanTab
            samples={samples}
            workOrderDocumentId={documentId}
            quotationUsesGroups={quotationUsesSampleGroups(wo.quotation ?? {})}
            layout="table"
          />
        )}
        {viewActiveTab === 'sampling_plan_air' && (
          <WorkOrderSamplingPlanTab
            samples={samples}
            workOrderDocumentId={documentId}
            quotationUsesGroups={quotationUsesSampleGroups(wo.quotation ?? {})}
            airGroup="air"
            layout="inline_form"
          />
        )}
        {viewActiveTab === 'sampling_plan_non_air' && (
          <WorkOrderSamplingPlanTab
            samples={samples}
            workOrderDocumentId={documentId}
            quotationUsesGroups={quotationUsesSampleGroups(wo.quotation ?? {})}
            airGroup="non_air"
            layout="inline_form"
          />
        )}
        {viewActiveTab === 'survey_team' && (
          <WorkOrderSurveyTeamTab
            workOrderDocumentId={documentId}
            samplingSchedule={wo.sampling_schedule}
          />
        )}
      </CollapsibleKeteranganSplit>
      <WorkOrderPdfPreviewDialog
        open={pdfOpen}
        workOrderDocumentId={documentId}
        jobNumber={wo.job_number}
        companyName={wo.quotation?.customer?.name}
        jwt={jwt}
        canSign={isMarketingPic(user)}
        showHtmlPreview={showCustomPdfButton}
        onClose={() => setPdfOpen(false)}
      />
    </PageShell>
  );
}
